import React, { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "./Popover";
import {
  VStack,
  HStack,
  ButtonSecondary,
  ButtonPrimary1,
} from "../../../../components/utils";
import { Button, ChevronDown, Search, X } from "./ComponentUtils";
import { Input, Label } from "../../../../components/shared/InputField";
import { Select } from "../../../../components/shared/Select";
import {
  OfferFilterV2,
  RecurringIntervalUnitV2,
} from "../../../../types/Transaction";
import { ParticipationConstraints } from "../../../../types/OptionBuyback";
import { FilterType } from "./FilterOptions";

const intervalUnits = [
  RecurringIntervalUnitV2.DAYS.toString(),
  RecurringIntervalUnitV2.MONTH.toString(),
  RecurringIntervalUnitV2.QUARTER.toString(),
  RecurringIntervalUnitV2.YEAR.toString(),
];

const ParticipationConstraintFilter = ({
  filter,
  formik,
  disableUpdate = false,
}: {
  filter: FilterType;
  formik: any;
  disableUpdate: boolean;
}) => {
  const isFilterActive = (filterId: string, filterType: string): boolean => {
    const filterValue =
      formik.values.offerFilter[filterId as keyof OfferFilterV2];

    return Array.isArray(filterValue) && filterValue.length > 0;
  };
  const [rows, setRows] = useState<ParticipationConstraints[]>(
    formik.values.offerFilter.participationConstraints &&
      formik.values.offerFilter.participationConstraints.length > 0
      ? formik.values.offerFilter.participationConstraints
      : [
          {
            maxFrequency: 0,
            interval: 0,
            intervalUnit: RecurringIntervalUnitV2.DAYS,
          },
        ]
  );

  const handleAddRow = () => {
    setRows([...rows, { maxFrequency: 0, interval: 0, intervalUnit: "Days" }]);
  };

  const handleInputChange = <K extends keyof ParticipationConstraints>(
    index: number,
    field: K,
    value: ParticipationConstraints[K]
  ) => {
    const updatedRows = [...rows];
    updatedRows[index][field] = value;
    setRows(updatedRows);
  };

  const handleApply = () => {
    const constraints = rows.filter(
      (row) => row.maxFrequency > 0 && row.interval > 0 && row.intervalUnit
    );

    formik.setFieldValue("offerFilter.participationConstraints", constraints);
  };

  const handleCancel = () => {
    setRows([
      {
        maxFrequency: 0,
        interval: 0,
        intervalUnit: RecurringIntervalUnitV2.DAYS,
      },
    ]);
  };

  return (
    <Popover
      content={
        <PopoverContent className="w-[400px]">
          <VStack className="gap-4 p-4">
            <h2 className="text-sm font-medium">Participation Constraints</h2>
            {rows.map((row, index) => (
              <VStack key={index} className="gap-4">
                <HStack key={index} className="gap-4">
                  <VStack className="gap-1">
                    <Label className="">Frequency</Label>
                    <Input
                      type="number"
                      value={row.maxFrequency === 0 ? "" : row.maxFrequency}
                      onChange={(e) =>
                        handleInputChange(
                          index,
                          "maxFrequency",
                          Number(e.target.value)
                        )
                      }
                      placeholder="Max Frequency"
                      className="w-24"
                    />
                  </VStack>
                  <VStack className="gap-1">
                    <Label className="">Interval</Label>
                    <Input
                      type="number"
                      value={row.interval === 0 ? "" : row.interval}
                      onChange={(e) =>
                        handleInputChange(
                          index,
                          "interval",
                          Number(e.target.value)
                        )
                      }
                      placeholder="Interval"
                      className="w-24"
                    />
                  </VStack>
                  <VStack className="gap-1">
                    <Label className="">Unit</Label>
                    <Select
                      value={row.intervalUnit}
                      onChange={(e) =>
                        handleInputChange(index, "intervalUnit", e.target.value)
                      }
                      options={intervalUnits.map((unit) => ({
                        value: unit,
                        label: unit,
                      }))}
                      textGetter={(option) => option.label}
                      valueGetter={(option) => option.value}
                      className="w-32"
                    />
                  </VStack>
                </HStack>
              </VStack>
            ))}
            <HStack className="justify-end">
              <ButtonPrimary1 onClick={handleAddRow} className="w-21">
                Add Row
              </ButtonPrimary1>
            </HStack>
            {!disableUpdate && (
              <HStack className="justify-between pt-3">
                <Button variant="outline" onClick={handleCancel}>
                  Clear
                </Button>
                <Button
                  className="text-white bg-orange-500 hover:bg-orange-600"
                  variant="primary"
                  onClick={handleApply}
                >
                  Apply
                </Button>
              </HStack>
            )}
          </VStack>
        </PopoverContent>
      }
    >
      <PopoverTrigger>
        <Button
          variant="outline"
          className={`flex items-center gap-2 ${
            isFilterActive(filter.id, filter.type)
              ? "bg-[#003366] text-white hover:bg-[#003366]"
              : ""
          }`}
        >
          {filter.label}
          {isFilterActive(filter.id, filter.type) &&
            (
              formik.values.offerFilter[
                filter.id as keyof OfferFilterV2
              ] as string[]
            ).length > 0 && (
              <span className="bg-white text-[#003366] rounded-full w-6 h-6 flex items-center justify-center">
                {
                  (
                    formik.values.offerFilter[
                      filter.id as keyof OfferFilterV2
                    ] as string[]
                  )?.length
                }
              </span>
            )}
          {(
            formik.values.offerFilter[
              filter.id as keyof OfferFilterV2
            ] as string[]
          )?.length > 0 && !disableUpdate ? (
            <X
              className="w-4 h-4 cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                formik.setFieldValue(`offerFilter.${filter.id}`, []); // Clear filter in Formik
              }}
            />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </Button>
      </PopoverTrigger>
    </Popover>
  );
};

export default ParticipationConstraintFilter;
